import type { ThemeConfig } from 'antd';
import { colors, typography } from '../../../tokens/semantic.tokens';

export const ANTD_THEME: ThemeConfig = {
  token: {
    colorPrimary: colors.primary.base,
    colorPrimaryHover: colors.primary.light,
    colorPrimaryActive: colors.primary.dark,
    colorPrimaryBg: colors.primary.lightest,
    colorPrimaryBgHover: colors.primary.lighter,
    colorPrimaryBorder: colors.primary.light,
    colorPrimaryTextActive: colors.primary.dark,
    colorPrimaryTextHover: colors.primary.light,

    colorSuccess: colors.success.base,
    colorSuccessHover: colors.success.dark,
    colorSuccessBorder: colors.success.light,
    colorSuccessBg: colors.success.lightest,

    colorWarning: colors.warning.base,
    colorWarningHover: colors.warning.light,
    colorWarningBorder: colors.warning.lighter,
    colorWarningBg: colors.warning.lightest,

    colorError: colors.error.base,
    colorErrorHover: colors.error.dark,
    colorErrorBorder: colors.error.lighter,
    colorErrorBg: colors.error.lightest,

    colorInfo: colors.info.base,
    colorInfoHover: colors.info.dark,
    colorInfoBorder: colors.info.lighter,
    colorInfoBg: colors.info.lightest,

    colorTextBase: colors.text.primary,
    colorTextSecondary: colors.text.secondary,
    colorTextTertiary: colors.text.tertiary,
    colorTextQuaternary: colors.text.disabled,
    colorTextDisabled: colors.text.disabled,

    colorLink: colors.info.base,
    colorLinkHover: colors.info.dark,
    colorLinkActive: colors.info.darker,

    colorBgBase: colors.background.default,
    colorBgContainer: colors.background.default,
    colorBgElevated: colors.background.elevated,
    colorBgLayout: colors.background.subtle,
    colorBgSpotlight: colors.neutral.darkest,
    colorBgTextHover: colors.neutral.lightest,
    colorBgTextActive: colors.neutral.lighter,
    colorBgMask: 'rgba(0, 0, 0, 0.45)',
    colorBgContainerDisabled: colors.neutral.lighter,

    colorBorder: colors.border.default,
    colorBorderSecondary: colors.border.subtle,
    colorBorderBg: colors.border.subtle,

    colorFillSecondary: colors.neutral.lightest,
    colorFillTertiary: colors.neutral.lighter,
    colorFillQuaternary: colors.neutral.light,
    colorSplit: colors.border.subtle,

    fontFamily: typography.body.md.regular.fontFamily,
    fontSize: Number.parseInt(typography.body.md.regular.fontSize),
    fontSizeLG: Number.parseInt(typography.body.lg.regular.fontSize),
    fontSizeSM: Number.parseInt(typography.body.sm.regular.fontSize),
    fontSizeXL: Number.parseInt(typography.heading.md.regular.fontSize),
    fontSizeHeading1: Number.parseInt(typography.heading.xl.regular.fontSize),
    fontSizeHeading2: Number.parseInt(typography.heading.lg.regular.fontSize),
    fontSizeHeading3: Number.parseInt(typography.heading.md.regular.fontSize),
    fontSizeHeading4: Number.parseInt(typography.heading.sm.regular.fontSize),
    fontSizeHeading5: Number.parseInt(typography.body.lg.regular.fontSize),

    lineHeight: Number.parseFloat(typography.body.md.regular.lineHeight) / Number.parseInt(typography.body.md.regular.fontSize),
    lineHeightLG: Number.parseFloat(typography.body.lg.regular.lineHeight) / Number.parseInt(typography.body.lg.regular.fontSize),
    lineHeightSM: Number.parseFloat(typography.body.sm.regular.lineHeight) / Number.parseInt(typography.body.sm.regular.fontSize),
    lineHeightHeading1:
      Number.parseFloat(typography.heading.xl.regular.lineHeight) / Number.parseInt(typography.heading.xl.regular.fontSize),
    lineHeightHeading2:
      Number.parseFloat(typography.heading.lg.regular.lineHeight) / Number.parseInt(typography.heading.lg.regular.fontSize),
    lineHeightHeading3:
      Number.parseFloat(typography.heading.md.regular.lineHeight) / Number.parseInt(typography.heading.md.regular.fontSize),
    lineHeightHeading4:
      Number.parseFloat(typography.heading.sm.regular.lineHeight) / Number.parseInt(typography.heading.sm.regular.fontSize),
    lineHeightHeading5: Number.parseFloat(typography.body.lg.regular.lineHeight) / Number.parseInt(typography.body.lg.regular.fontSize)
  },

  components: {
    Button: {
      colorPrimary: colors.info.dark,
      colorPrimaryHover: colors.info.base,
      colorPrimaryActive: colors.info.darker,
      colorPrimaryBorder: colors.info.dark,
      defaultShadow: 'none',
      primaryShadow: 'none',
      textHoverBg: colors.neutral.lightest,
      colorBgTextActive: colors.neutral.lighter
    },

    Typography: {
      fontFamily: typography.body.md.regular.fontFamily,
      fontSizeHeading1: Number.parseInt(typography.heading.xl.regular.fontSize),
      fontSizeHeading2: Number.parseInt(typography.heading.lg.regular.fontSize),
      fontSizeHeading3: Number.parseInt(typography.heading.md.regular.fontSize),
      fontSizeHeading4: Number.parseInt(typography.heading.sm.regular.fontSize),
      fontSizeHeading5: Number.parseInt(typography.body.lg.regular.fontSize),
      titleMarginBottom: '0',
      titleMarginTop: '0'
    },

    Tabs: {
      itemSelectedColor: colors.info.base,
      itemHoverColor: colors.info.base,
      itemActiveColor: colors.info.base,
      inkBarColor: colors.info.base
    },

    DatePicker: {
      fontFamily: typography.body.md.regular.fontFamily
    },

    Checkbox: {
      colorBorder: colors.border.default,
      colorBgContainer: colors.background.default,
      colorPrimary: colors.info.base,
      colorPrimaryBorder: colors.info.base,
      colorPrimaryHover: colors.info.light,
      colorBgContainerDisabled: colors.neutral.lighter,
      colorTextDisabled: colors.text.disabled
    },

    Radio: {
      colorBorder: colors.border.default,
      colorBgContainer: colors.background.default,
      colorPrimary: colors.info.base,
      colorPrimaryBorder: colors.info.base,
      colorPrimaryHover: colors.info.light,
      colorBgContainerDisabled: colors.neutral.lighter,
      colorTextDisabled: colors.text.disabled
    },

    Select: {
      colorBorder: colors.border.default,
      colorBgContainer: colors.background.default,
      colorText: colors.text.primary,
      colorTextPlaceholder: colors.text.tertiary,
      colorPrimaryHover: colors.info.light,
      colorPrimary: colors.info.light,
      colorBgContainerDisabled: colors.neutral.lighter,
      colorTextDisabled: colors.text.secondary,
      controlOutline: colors.info.light,
      optionSelectedBg: colors.info.lightest,
      optionSelectedColor: colors.text.primary,
      optionActiveBg: colors.neutral.lightest,
      colorTextQuaternary: colors.text.tertiary
    }
  }
};
